import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

dayjs.extend(utc)

const createUserMessage = (threadId, prompt, attachments = []) => ({
  threadId,
  threadMessageId: null,
  actor: 'user',
  message: prompt,
  data: {},
  isGenerating: false,
  generationStatus: null,
  responseId: null,
  attachments,
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

const createAgentPlaceholderMessage = (threadId, responseId) => ({
  threadId,
  threadMessageId: responseId,
  actor: 'assistan',
  message: '',
  data: {},
  isGenerating: true,
  generationStatus: 'Thinking...',
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

// List all user threads
export const useListAIUserThreads = (options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, categories } = options

  return useQuery({
    queryKey: ['denali/ai-user-threads', userId, categories],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-ai-user-threads', {
        categories: categories || ['assistant'] // Default to assistant category
      })
      return data
    },
    enabled
  })
}

// Get a specific thread
export const useAIThread = (threadId, options = {}) => {
  const { enabled = true, refetchInterval = null, includeMessages = true, includeAttachments = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread', threadId, includeMessages, includeAttachments],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread', {
        threadId,
        categories: ['assistant'],
        includeMessages,
        includeAttachments
      })
      return data
    },
    enabled: enabled && !!threadId,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval,
    staleTime: 0 // Allow using cached data but mark as stale so it can be updated
  })
}

// Get a specific thread
export const useRenameAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-rename-thread', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-thread', command)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

// Get a specific thread message
export const useAIThreadMessage = (threadId, threadMessageId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-message', threadId, threadMessageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
        threadId,
        threadMessageId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageId
  })
}

// Get a specific message by flexible parent type
export const useAiMessage = (parentInfo, messageId, options = {}) => {
  const { enabled = true } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled
  })
}

// Get message events by flexible parent type
export const useAiMessageEvents = (parentInfo, messageId, options = {}) => {
  const { enabled = true, refetchInterval } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message-events', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message-events', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled,
    refetchInterval
  })
}

// Get thread attachment
export const useAIThreadAttachment = (threadId, threadMessageAttachmentId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-attachment', threadId, threadMessageAttachmentId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-attachment', {
        threadId,
        threadMessageAttachmentId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageAttachmentId
  })
}

export const useAIToolCallDetails = (threadId, messageId, toolCallId, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['denali/ai-tool-call-details', threadId, messageId, toolCallId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-tool-call-details', {
        threadId,
        messageId,
        toolCallId
      })
      return data
    },
    enabled: enabled && !!threadId && !!messageId && !!toolCallId
  })
}

export const useListUserScheduledPrompts = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-user-scheduled-prompts'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-user-scheduled-prompts', {
      })
      return data
    },
    enabled: enabled
  })
}

export const useScheduledPromptById = (scheduleId, include, options = {}) => {
  const { enabled = true } = options
  const includeKey = include ? JSON.stringify(include) : null

  return useQuery({
    queryKey: ['denali/get-scheduled-prompt-by-id', scheduleId, includeKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-scheduled-prompt-by-id', {
        scheduleId,
        include
      })
      return data
    },
    enabled: enabled
  })
}

export const useCreateAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-prompt', scheduledPrompt)

      return data
    }
  })
}

export const useModifyAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledDigest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (digest) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-digest', digest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateAiScheduledDigest = () => {
  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-digest', scheduledDigest)

      return data
    }
  })
}

export const useModifyAiScheduledDigest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-digest', scheduledDigest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRunAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-run-scheduled-prompt', command)

      return data
    }
  })
}

export const useSearchAIUserThreads = (query = {}, options = {}) => {
  const { enabled = true } = options
  const queryKey = query ? JSON.stringify(query) : '{}'

  return useQuery({
    queryKey: ['denali/search-ai-user-threads', queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', query)
      return data
    },
    enabled: enabled
  })
}

export const useSearchAIUserThreadsInfinite = (query, options = {}) => {
  const { enabled = true, pageSize = 10 } = options
  const { searchString, orderBy } = query

  return useInfiniteQuery({
    queryKey: ['denali/search-ai-user-threads-infinite', searchString, orderBy],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', {
        searchString,
        orderBy,
        skip: pageParam,
        take: pageSize
      })
      return data
    },
    enabled,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than pageSize, we've reached the end
      if (!lastPage?.data || lastPage.data.length < pageSize) {
        return undefined
      }
      // Calculate next skip value
      return allPages.length * pageSize
    }
  })
}

export const useCountAiUserThreads = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/count-ai-user-threads'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'count-ai-user-threads', {})
      return data?.count
    },
    enabled: enabled
  })
}

export const useUserReadingList = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-user-reading-list'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-user-reading-list', {})
      return data
    },
    enabled: enabled
  })
}

// Invoke the Denali AI
export const useInvokeDenaliAssistant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prompt, threadId, context, options, attachments }) => {
      if (threadId) {
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev) {
            return {
              thread: {
                threadId,
                messages: [createUserMessage(threadId, prompt, attachments)]
              }
            }
          }
          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages: [...prev.thread.messages, createUserMessage(threadId, prompt, attachments)]
            }
          }
        })
      }

      const request = {
        prompt,
        attachments,
        ...(threadId && { threadId }),
        ...(context && { context }),
        ...(options && { options })
      }

      const { data } = await postNamedCommand('denali', 'invoke-denali-assistant', request)

      return data
    },
    onSuccess: async (data, { threadId: originalThreadId, attachments: sentAttachments }) => {
      const threadId = data.threadId || originalThreadId
      const newMessage = data.message

      queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
        if (!prev) {
          return {
            thread: {
              // Spread the full thread object from response (includes metadata, name, etc.)
              ...(data.thread || {}),
              threadId,
              messages: [{
                ...newMessage,
                attachments: newMessage.attachments || sentAttachments || []
              }, createAgentPlaceholderMessage(threadId, data.responseId)]
            }
          }
        }

        // Replace placeholder user message with actual message if it exists
        const messages = [...(prev.thread.messages || [])]
        const placeholderIndex = messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
        if (placeholderIndex !== -1) {
          const placeholderMessage = messages[placeholderIndex]
          messages[placeholderIndex] = {
            ...newMessage,
            attachments: newMessage.attachments || placeholderMessage.attachments || []
          }
        } else {
          messages.push({
            ...newMessage,
            attachments: newMessage.attachments || sentAttachments || []
          })
        }

        // Add agent placeholder message
        messages.push(createAgentPlaceholderMessage(threadId, data.responseId))

        return {
          ...prev,
          thread: {
            // Merge thread properties from response (including metadata)
            ...prev.thread,
            ...(data.thread || {}),
            // Ensure messages are updated
            messages
          }
        }
      })

      // Also invalidate the threads list to pick up the new thread
      queryClient.invalidateQueries(['denali/ai-user-threads'])
    }
  })
}

// Fetch tool call summary for a specific message (lazy load for completed messages)
export const useToolCallSummary = (messageId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/tool-call-summary', messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-tool-call-summary', {
        messageId
      })
      return data.summary
    },
    enabled: enabled && !!messageId,
    staleTime: Infinity // Summary won't change for completed messages
  })
}

// Poll for message generation status
export const useMessagePolling = (message) => {
  const queryClient = useQueryClient()
  const refetchInterval = message?.isGenerating ? 1000 : null

  return useQuery({
    queryKey: ['denali/ai-thread-message-poll', message?.threadId, message?.threadMessageId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
          threadId: message.threadId,
          threadMessageId: message.threadMessageId,
          categories: ['assistant'],
          includeAttachments: true,
          includeToolCallSummary: true
        })

        return data
      } catch (error) {
        // 204 or 404 means message is still being generated
        if (error.response?.status === 204 || error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!message?.isGenerating && !!message?.threadId && !!message?.threadMessageId,
    refetchInterval,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (!data?.message) return

      // Merge toolCallSummary into message.data for downstream components
      const newMessage = {
        ...data.message,
        data: {
          ...data.message.data,
          toolCallSummary: data.toolCallSummary
        }
      }

      queryClient.setQueryData(['denali/ai-thread', message.threadId, true, true], prev => {
        if (!prev) return prev

        const messages = [...prev.thread.messages]
        const idx = messages.findIndex(m => m.threadMessageId === message.threadMessageId)
        if (idx > -1) {
          messages[idx] = newMessage
        }

        return {
          ...prev,
          thread: {
            ...prev.thread,
            messages
          }
        }
      })
    }
  })
}

// Note: Threads are created automatically when the first message is sent
// This is just a placeholder for potential future use
export const useCreateAIThread = () => {
  return {
    mutateAsync: async ({ title }) => {
      // Threads are created server-side when invoking the assistant
      // This is just a placeholder that returns a temporary ID
      return {
        threadId: null,
        title: title || 'New Chat',
        createdDate: dayjs.utc().toISOString()
      }
    }
  }
}

export const useGetAiUserPreferences = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-user-preferences'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-user-preferences', {
        includeSchema: true
      })
      return data
    },
    enabled: enabled
  })
}

export const useModifyAiUserPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (changes) => {
      const { data } = await postNamedCommand('denali', 'modify-ai-user-preferences', { changes })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-user-preferences'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useGetAiModels = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-models'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-models', {
        includeProvider: true,
        includeTasks: true,
        hidden: false
      })
      return data
    },
    enabled: enabled
  })
}

export const useGetAiGuardrails = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-guardrails'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-guardrails', {})
      return data
    },
    enabled: enabled
  })
}

export const useListAddressableInternalUsers = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-addressable-internal-users'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-addressable-internal-users', {})

      return data
    },
    enabled
  })
}

export const useCopyScheduledPrompt = () => {
  return useMutation({
    mutationFn: async ({ scheduleId, userIds }) => {
      const { data } = await postNamedCommand('denali', 'ai-copy-scheduled-prompt', {
        scheduleId,
        userIds
      })

      return data
    }
  })
}

export const useGetAiUsage = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-usage'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-usage', {})
      return data
    },
    enabled: enabled
  })
}

export const useListAiFeedbackMetrics = (metricSentiment, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-ai-feedback-metrics', metricSentiment],
    queryFn: async () => {
      const { data: body } = await postNamedQuery('denali', 'list-ai-feedback-metrics', {
        metricSentiment
      })
      // Normalize {data} envelope used by named queries
      return body?.data ?? []
    },
    enabled: enabled && !!metricSentiment,
    staleTime: 1000 * 60 * 5
  })
}

// Provide feedback on a message
export const useProvideFeedback = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadMessageId, type, feedbackText, threadId, feedbackMetricId }) => {
      const command = {
        threadMessageId,
        type,
        feedbackMetricId
      }
      if (feedbackText) {
        command.feedbackText = feedbackText
      }
      const { data } = await postNamedCommand('denali', 'ai-provide-feedback', command)
      return { ...data, threadMessageId, type, feedbackText, threadId, feedbackMetricId }
    },
    onSuccess: (data) => {
      if (data.threadId) {
        queryClient.setQueryData(['denali/ai-thread', data.threadId, true, true], prev => {
          if (!prev) return prev

          const messages = [...(prev.thread.messages || [])]
          const messageIndex = messages.findIndex(m => m.threadMessageId === data.threadMessageId)

          if (messageIndex > -1) {
            messages[messageIndex] = {
              ...messages[messageIndex],
              feedback: {
                type: data.type,
                ...(data.feedbackText && { feedbackText: data.feedbackText }),
                ...(data.feedbackMetricId != null && { feedbackMetricId: data.feedbackMetricId })
              }
            }
          }

          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages
            }
          }
        })
      }
    }
  })
}

// Submit a denali action (form submission, questionnaire, etc.)
// Optionally continues the conversation by passing a follow-up prompt.
export const useSubmitDenaliAction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    onMutate: async ({ prompt, message }) => {
      const threadId = message?.threadId
      if (!prompt || !threadId) return {}

      const threadKey = ['denali/ai-thread', threadId, true, true]
      await queryClient.cancelQueries(threadKey)
      const previousData = queryClient.getQueryData(threadKey)

      queryClient.setQueryData(threadKey, prev => {
        if (!prev) return prev
        return {
          ...prev,
          thread: {
            ...prev.thread,
            messages: [...prev.thread.messages, createUserMessage(threadId, prompt)]
          }
        }
      })

      return { previousData, threadId }
    },
    mutationFn: async ({ actionTraceId, input, prompt, options }) => {
      const request = { actionTraceId, input }
      if (prompt) request.prompt = prompt
      if (options) request.options = options

      const { data } = await postNamedCommand('denali', 'submit-denali-action', request)
      return data
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData && context?.threadId) {
        queryClient.setQueryData(
          ['denali/ai-thread', context.threadId, true, true],
          context.previousData
        )
      }
    },
    onSuccess: (data, { message, prompt }) => {
      if (!message) return

      const threadId = message.threadId
      const messageId = message.threadMessageId

      // On idempotent retry or failed follow-up, remove placeholder and refetch
      if (data?.alreadyCompleted || data?.followUpFailed) {
        if (threadId) {
          queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
            if (!prev?.thread?.messages) return prev
            return {
              ...prev,
              thread: {
                ...prev.thread,
                messages: prev.thread.messages.filter(m => !m.isPlaceholder)
              }
            }
          })
          queryClient.invalidateQueries(['denali/ai-thread', threadId])
        }
        return
      }

      // Update action status on the source assistant message
      if (data?.actions && threadId && messageId) {
        const actionsData = data.actions
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], (oldData) => {
          if (!oldData || !oldData.thread || !oldData.thread.messages) return oldData
          return {
            ...oldData,
            thread: {
              ...oldData.thread,
              messages: oldData.thread.messages.map(msg =>
                msg.threadMessageId === messageId
                  ? {
                    ...msg,
                    data: {
                      ...(msg.data || {}),
                      actions: {
                        ...(msg.data?.actions || {}),
                        ...actionsData
                      }
                    }
                  }
                  : msg
              )
            }
          }
        })
      }

      // If the server created a follow-up message, update the thread cache
      if (data?.message && data?.responseId && threadId) {
        const newMessage = data.message

        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev?.thread?.messages) return prev

          const messages = [...prev.thread.messages]
          const placeholderIndex = messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
          if (placeholderIndex !== -1) {
            messages[placeholderIndex] = {
              ...newMessage,
              attachments: []
            }
          } else {
            messages.push({ ...newMessage, attachments: [] })
          }

          messages.push(createAgentPlaceholderMessage(threadId, data.responseId))

          return {
            ...prev,
            thread: { ...prev.thread, messages }
          }
        })

        queryClient.invalidateQueries(['denali/ai-user-threads'])
      }

      // Update single message query if we have parentInfo
      if (data?.actions && message.parentInfo && messageId) {
        const parentKey = JSON.stringify(message.parentInfo)
        queryClient.setQueryData(['denali/ai-message', parentKey, messageId], (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: {
              ...(oldData.data || {}),
              actions: {
                ...(oldData.data?.actions || {}),
                ...data.actions
              }
            }
          }
        })
      }
    }
  })
}

// Upload attachment - gets presigned URL and uploads file to S3
export const useUploadAttachment = () => {
  return useMutation({
    mutationFn: async ({ fileName, contentType }) => {
      const { data } = await postNamedCommand('denali', 'ai-upload-attachment', {
        fileName,
        contentType
      })
      return data
    }
  })
}

// Retry a message - deletes the message and all after it, then reprocesses the last user message
export const useRetryMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadId, threadMessageId }) => {
      const { data } = await postNamedCommand('denali', 'retry-denali-assistant', {
        threadId,
        threadMessageId
      })
      return data
    },
    onSuccess: async (data, { threadId, threadMessageId }) => {
      // If the backend returns the updated thread directly, use it
      if (data?.thread) {
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], data)
        return
      }

      // If responseId is returned, optimistically add a placeholder message to start polling
      if (data?.responseId) {
        const placeholder = createAgentPlaceholderMessage(threadId, data.responseId)

        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev) {
            return {
              thread: {
                threadId,
                messages: [placeholder]
              }
            }
          }

          const messages = [...(prev.thread.messages || [])]
          const retryIndex = messages.findIndex(m => m.threadMessageId === threadMessageId)

          if (retryIndex !== -1) {
            messages.splice(retryIndex)
          }

          messages.push(placeholder)

          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages
            }
          }
        })
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['denali/ai-thread', threadId, true, true] })
    }
  })
}

export const useCancelDenaliAssistant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadId }) => {
      const { data } = await postNamedCommand('denali', 'cancel-denali-assistant', {
        threadId
      })
      return data
    },
    onSuccess: async (data, { threadId }) => {
      await queryClient.invalidateQueries({ queryKey: ['denali/ai-thread', threadId, true, true] })
    }
  })
}

export const useGetAiSkills = (options = {}) => {
  const { enabled = true, skillIds, allVersions = false, includeAll = false, skip = 0, take = 1000 } = options
  const queryKey = skillIds ? JSON.stringify(skillIds.sort()) : null

  return useQuery({
    queryKey: ['denali/get-ai-skills', queryKey, allVersions, includeAll, skip, take],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-skills', {
        ...(skillIds && { skillIds }),
        allVersions,
        includeAll,
        skip,
        take
      })
      return data
    },
    enabled
  })
}

export const useAIThreadAudit = (threadId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-audit', threadId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-audit', { threadId })
      return data
    },
    enabled: enabled && !!threadId,
    cacheTime: 0,
    staleTime: 0,
    refetchOnMount: 'always',
    keepPreviousData: false
  })
}

export const useInsertAiSkill = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ skillId, skillName, skillContent }) => {
      const { data } = await postNamedCommand('denali', 'ai-insert-skill', {
        skillId,
        skillName,
        skillContent
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skills'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tags'] }).catch(console.error)
    }
  })
}

export const useGetAiSkill = (skillId, options = {}) => {
  const { enabled = true, version, tag } = options
  return useQuery({
    queryKey: ['denali/get-ai-skill', skillId, version, tag],
    queryFn: async () => {
      const body = { skillId }
      if (version != null) body.version = version
      if (tag != null) body.tag = tag
      const { data } = await postNamedQuery('denali', 'get-ai-skill', body)
      return data
    },
    enabled: enabled && !!skillId
  })
}

export const useGetAiSkillTags = (skillId, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['denali/get-ai-skill-tags', skillId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-skill-tags', { skillId })
      return data
    },
    enabled: enabled && !!skillId
  })
}

export const useCreateAiSkillTag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ skillId, tagName, version, comment, ticketNumber }) => {
      const { data } = await postNamedCommand('denali', 'ai-create-skill-tag', { skillId, tagName, version, comment, ticketNumber })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tags'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tag-history'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skills'] }).catch(console.error)
    }
  })
}

export const usePromoteAiSkillTag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ skillId, tagName, version, comment, ticketNumber }) => {
      const { data } = await postNamedCommand('denali', 'ai-promote-skill-tag', { skillId, tagName, version, comment, ticketNumber })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tags'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tag-history'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skills'] }).catch(console.error)
    }
  })
}

export const useDeleteAiSkillTag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ skillId, tagName, comment, ticketNumber }) => {
      const { data } = await postNamedCommand('denali', 'ai-delete-skill-tag', { skillId, tagName, comment, ticketNumber })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tags'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skill-tag-history'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skills'] }).catch(console.error)
    }
  })
}

export const useGetAiSkillTagHistory = (skillId, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['denali/get-ai-skill-tag-history', skillId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-skill-tag-history', { skillId })
      return data
    },
    enabled: enabled && !!skillId
  })
}

/**
 * Step 1: Calls denali to merge template + content → uploads HTML to S3.
 * Returns { bucket, type, id, title, artifactType, options }.
 */
export const usePrepareArtifactPdf = () => {
  return useMutation({
    mutationFn: async ({ threadId, messageId, attachmentId }) => {
      const { data } = await postNamedCommand('denali', 'render-artifact-pdf', {
        threadId,
        messageId,
        ...(attachmentId && { attachmentId })
      })
      return data
    }
  })
}

function unwrapLambdaResponse (data) {
  if (data?.body && typeof data.body === 'string') {
    try { return JSON.parse(data.body) } catch (e) { /* fall through */ }
  }
  return data
}

/**
 * Step 2: Calls reports-app to trigger the Puppeteer render.
 * Returns { jobId }.
 */
export const useTriggerRenderPdf = () => {
  return useMutation({
    mutationFn: async ({ bucket, type, id, title, options }) => {
      const { data } = await postNamedCommand('reports', 'render-pdf', {
        bucket,
        type,
        id,
        title,
        options
      })
      return unwrapLambdaResponse(data)
    }
  })
}

/**
 * Step 3: Polls reports-app for render status + download URL.
 */
export const useGetRenderPdfStatus = (jobId, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['reports/get-render-pdf-status', jobId],
    queryFn: async () => {
      const { data } = await postNamedQuery('reports', 'get-render-pdf-status', { jobId })
      return unwrapLambdaResponse(data)
    },
    enabled: enabled && !!jobId,
    refetchInterval: (data) => {
      const status = data?.status
      if (status === 'complete' || status === 'error') return false
      return 3000
    }
  })
}
